import { useEffect, useRef, useState } from 'react';
import { MapPin, ExternalLink, /* Store */ } from 'lucide-react';
import type { Embajador } from '@/types';

interface PuntoVenta {
  id: string | number;
  direccion: string;
  ciudad: string;
  google_maps_url: string;
}

interface PuntosVentaSectionProps {
  puntosVenta: PuntoVenta[];
  ambassador: Embajador | null;
}

export default function PuntosVentaSection({ puntosVenta, ambassador }: PuntosVentaSectionProps) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  // Animación al hacer scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Si no hay puntos de venta, no renderizar la sección
  if (!puntosVenta || puntosVenta.length === 0) {
    return null;
  }

  // Función para asegurar que la URL sea absoluta
  const formatUrl = (url: string) => {
    if (!url) return '';
    return url.startsWith('http') ? url : `https://${url}`;
  };

  return (
    <section
      id="puntos-venta"
      ref={sectionRef}
      className="py-24 bg-[#f8f9f5] relative overflow-hidden"
    >
      {/* Fondo decorativo */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-[#8abd31]/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-64 h-64 bg-[#e91e8c]/5 rounded-full blur-3xl pointer-events-none" />

      <div className="section-padding relative z-10">
        <div className="max-w-7xl mx-auto">
          {/* Header de la sección */}
          <div
            className={`text-center mb-16 transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            {/* <div className="inline-flex items-center px-4 py-2 bg-[#8abd31]/10 rounded-full mb-4">
              <Store className="w-4 h-4 text-[#8abd31] mr-2" />
              <span className="text-sm font-medium text-[#8abd31]">Encuéntranos</span>
            </div> */}
            <h2 className="font-serif text-4xl md:text-5xl text-[#8abd31] mb-4">
              Puntos de <span className="text-[#e91e8c]">Venta</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Visita nuestros puntos de venta autorizados y descubre los productos Ismerely en persona.
              ¡Te esperamos!
            </p>
          </div>

          {/* Grid de puntos de venta - adaptable a cualquier cantidad */}
          <div
            className={`grid gap-6 transition-all duration-700 delay-200 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            } ${
              // Clases de grid según la cantidad de puntos
              puntosVenta.length === 1
                ? 'grid-cols-1 max-w-lg mx-auto'
                : puntosVenta.length === 2
                ? 'grid-cols-1 md:grid-cols-2 max-w-4xl mx-auto'
                : puntosVenta.length === 3
                ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
                : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
            }`}
          >
            {puntosVenta.map((punto, index) => (
              <div
                key={punto.id}
                className="group relative bg-white rounded-2xl p-6 transition-all duration-500 hover:-translate-y-2 hover:shadow-xl"
                style={{
                  boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                  transitionDelay: isVisible ? `${index * 100}ms` : '0ms',
                }}
              >
                {/* Indicador decorativo superior */}
                <div className="absolute top-0 left-6 right-6 h-1 bg-gradient-to-r from-[#8abd31] to-[#e91e8c] rounded-full transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />

                {/* Icono de ubicación */}
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#8abd31]/15 to-[#e91e8c]/15 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300">
                  <MapPin className="w-7 h-7 text-[#e91e8c]" />
                </div>

                {/* Información del punto de venta */}
                {/* Se utiliza el nombre de la ciudad como título */}
                <h3 className="font-serif text-xl text-[#8abd31] mb-2 group-hover:text-[#e91e8c] transition-colors duration-300">
                  {punto.ciudad}
                </h3>

                <div className="space-y-3">
                  {/* Dirección */}
                  <div className="flex items-start gap-3">
                    <MapPin className="w-4 h-4 text-[#8abd31] mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {punto.direccion}
                    </p>
                  </div>
                </div>

                {/* Botón de Google Maps */}
                {punto.google_maps_url && (
                  <a
                    href={formatUrl(punto.google_maps_url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-[#e91e8c] hover:text-[#8abd31] transition-colors group/link"
                  >
                    <span>Ver en Google Maps</span>
                    <ExternalLink className="w-4 h-4 transition-transform group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5" />
                  </a>
                )}

                {/* Decoración de fondo */}
                <div className="absolute -bottom-2 -right-2 w-20 h-20 bg-[#8abd31]/5 rounded-full blur-xl pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </div>
            ))}
          </div>

          {/* Mensaje adicional */}
          <div
            className={`mt-12 text-center transition-all duration-700 delay-400 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <p className="text-sm text-gray-500">
              ¿No encuentras un punto de venta cerca de ti?{' '}
              {ambassador?.whatsapp_number ? (
                <a
                  href={`https://wa.me/${ambassador.whatsapp_number.toString().replace(/\D/g, '')}?text=${encodeURIComponent(
                    `Hola${ambassador.nombre_completo ? ` ${ambassador.nombre_completo}` : ''}, me comunico desde la plataforma de Volando Alto`
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#e91e8c] font-medium hover:underline hover:text-[#8abd31] transition-colors"
                >
                  Contáctanos para más información
                </a>
              ) : (
                <span className="text-[#e91e8c] font-medium">
                  Contáctanos para más información
                </span>
              )}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
